// components/private/StepNavigation.tsx
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Card } from "../ui/card";
import { useQueryClient } from "@tanstack/react-query";

interface StepNavigationProps {
    onNext?: () => Promise<void> | void;
    onBack?: () => Promise<void> | void;
    nextDisabled?: boolean;
    backDisabled?: boolean;
}

export default function StepNavigation({
    onNext,
    onBack,
    nextDisabled = false,
    backDisabled = false,
}: StepNavigationProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();

    const steps = [
        { path: "/order/paket" },
        { path: "/order/konfirmasi" },
        { path: "/order/pembayaran" },
        { path: "/order/pembayaran/status" },
        { path: "/order/kartu" },
    ];

    const currentIndex = steps.findIndex((s) => s.path === location.pathname);
    const progress = ((currentIndex + 1) / steps.length) * 100;

    const clearCaches = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["registration-status"] }),
            queryClient.invalidateQueries({ queryKey: ["registration-step"] }),
            queryClient.invalidateQueries({ queryKey: ["registration-check"] }),
            queryClient.invalidateQueries({ queryKey: ["payment-check"] })
        ]);
    };

    const goNext = async () => {
        if (isLoading || nextDisabled) return;
        setIsLoading(true);
        try {
            if (onNext) await onNext();
            await clearCaches();
            if (currentIndex < steps.length - 1) {
                navigate(steps[currentIndex + 1].path, { replace: true });
            }
        } catch (error) {
            console.error("Navigation Next Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const goBack = async () => {
        if (isLoading || backDisabled || currentIndex === 0) return;
        setIsLoading(true);
        try {
            if (onBack) await onBack();
            await clearCaches();
            navigate(steps[currentIndex - 1].path, { replace: true });
        } catch (error) {
            console.error("Navigation Back Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4 mt-8 sticky bottom-4 z-50 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Card className="border shadow-2xl dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md p-5 gap-3 rounded-2xl">
                <div className="flex justify-between items-center mb-4">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={goBack}
                        disabled={currentIndex === 0 || isLoading || backDisabled}
                        className={`flex items-center gap-2 min-w-[120px] font-bold bg-blue-50 hover:bg-blue-50 dark:bg-slate-700 dark:hover:bg-slate-700 ${backDisabled ? 'opacity-30' : ''}`}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Sebelumnya
                    </Button>

                    <Button
                        type="button"
                        onClick={goNext}
                        disabled={nextDisabled || currentIndex === steps.length - 1 || isLoading}
                        className="flex items-center gap-3 min-w-[120px] bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-200 dark:shadow-none"
                    >
                        {isLoading ? (
                            <>
                                Memproses
                                <Loader2 className="w-4 h-4 animate-spin" />
                            </>
                        ) : (
                            <>
                                Selanjutnya
                                <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </Button>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex-1 bg-slate-100 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                        <div
                            className="bg-blue-600 h-full transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-[10px] font-black text-slate-400 w-8">
                        {Math.round(progress)}%
                    </span>
                </div>
            </Card>
        </div>
    );
}